/* =============================================================================
   VIEWS / ADMIN / DAILY-SHEET — Tagesblatt-Druckpopup
   =============================================================================
   Öffnet ein eigenes Fenster mit einer druckoptimierten HTML-Seite, das
   das Tagesblatt für einen gewählten Tag zeigt (Standard: heute).
============================================================================ */

import {
  todayISO, showToast,
  normalizePreorderItems, preorderSpecialRequests
} from '../../utils/helpers.js';
import {
  totalCapacity,
  computeDayUtilization,
  isActiveReservation
} from '../../core/capacity.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [], settings: {} };
}

export function openDailySheet(dateStr) {
  const state = getState();
  const targetDate = dateStr || todayISO();
  const day = (state.reservations || [])
    .filter(r => r.date === targetDate && isActiveReservation(r))
    .sort((a, b) => a.time.localeCompare(b.time));

  // S61 — Walk-Ins für das Daily-Sheet: eigene Sub-Sektion unter den
  // Reservations. Quelle: table_occupancies, die den Tag berühren (3-Pfad-
  // Garantie aus S60). Filter analog zu occupancy.js renderTimeline.
  const dayStartMs = new Date(targetDate + 'T00:00:00').getTime();
  const dayEndMs   = dayStartMs + 24 * 60 * 60 * 1000;
  const walkIns = ((state.tableOccupancies || []).filter(o => {
    if (o.deletedAt || !o.startedAt) return false;
    if (o.status === 'cancelled') return false;
    const startMs = new Date(o.startedAt).getTime();
    if (isNaN(startMs)) return false;
    const durMs = (Number(o.durationMin) || 90) * 60 * 1000;
    const endMs = startMs + durMs;
    return startMs < dayEndMs && endMs > dayStartMs;
  })).sort((a, b) => {
    const ta = new Date(a.startedAt).getTime();
    const tb = new Date(b.startedAt).getTime();
    return ta - tb;
  });
  const walkInGuests = walkIns.reduce((s, o) => s + (Number(o.guests) || 0), 0);

  const totalGuests = day.reduce((s, r) => s + r.guests, 0);
  const restName = state.settings.restaurantName || 'Grill & Green';
  const cap = totalCapacity();
  const util = Math.round(computeDayUtilization(targetDate) * 100);

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  const rows = day.map(r => {
    const tables = (r.assignedTables || []).map(id =>
      (state.tables.find(t => t.id === id) || {}).name || '?'
    ).join(' + ');
    const tagBits = [];
    if (r.tablesCombined) tagBits.push('kombiniert');
    const usedChairs = Number(r.extraChairsUsed) || (r.needsExtraChair ? 1 : 0);
    if (usedChairs > 0) tagBits.push(`+${usedChairs} Stuhl${usedChairs > 1 ? 'e' : ''}`);
    // S60 / 3-Pfad: Walk-In-Tag aus dem Reservations-Pfad entfernt — Walk-Ins
    // sind seit phase_51 eigene Bewegungsdaten in table_occupancies. Eine
    // dedizierte Walk-In-Sektion im Daily-Sheet kommt in S61 (Polish).
    if (r.requiresManualConfirm && r.status === 'pending') tagBits.push('PRÜFEN');
    const tagsStr = tagBits.length ? ' · ' + tagBits.join(' · ') : '';
    const statusLabel = {
      pending: 'OFFEN',
      confirmed: 'best.',
      waitlist: 'WL'
    }[r.status] || r.status;

    return `
      <tr>
        <td class="t-time">${r.time}</td>
        <td class="t-name">
          <strong>${escapeHtml(r.name)}</strong>${tagsStr}
          ${r.phone ? `<div class="t-meta">☎ ${escapeHtml(r.phone)}</div>` : ''}
          ${r.notes ? `<div class="t-notes">📝 ${escapeHtml(r.notes)}</div>` : ''}
          ${(() => {
            const items = normalizePreorderItems(r.preorder);
            if (!items.length) return '';
            const lines = items.map(it => {
              const main = `${it.qty}× ${escapeHtml(it.displayName)}`;
              return it.note ? `${main} <span style="font-size:.85em; color:var(--ink-soft);">— ${escapeHtml(it.note)}</span>` : main;
            }).join('<br>');
            const special = preorderSpecialRequests(r.preorder);
            const specBlock = special ? `<div style="font-weight:600; margin-top:2px;">⚠ ${escapeHtml(special)}</div>` : '';
            return `<div class="t-preorder"><strong>Vorbestellung:</strong><br>${lines}${specBlock}</div>`;
          })()}
        </td>
        <td class="t-guests">${r.guests} Pers.</td>
        <td class="t-tables">${tables || '<em>-</em>'}</td>
        <td class="t-status">${statusLabel}</td>
        <td class="t-check"></td>
      </tr>
    `;
  }).join('');

  // S61 — Walk-In-Zeilen (eigene Sektion). Format leicht abgesetzt: keine
  // "Status"-Spalte (Status leitet sich vom Started/Ended-Stempel ab), aber
  // dafür Belegt-seit-Zeit + Belegt-Status (active/completed/cancelled).
  const walkInRows = walkIns.map(o => {
    const startDate = new Date(o.startedAt);
    const startTime = `${String(startDate.getHours()).padStart(2,'0')}:${String(startDate.getMinutes()).padStart(2,'0')}`;
    const dur = Number(o.durationMin) || 90;
    const endDate = new Date(startDate.getTime() + dur * 60000);
    const endTime = `${String(endDate.getHours()).padStart(2,'0')}:${String(endDate.getMinutes()).padStart(2,'0')}`;
    const tableName = (() => {
      // Tisch-Name(n) auflösen: primärer Tisch oder assignedTableIds-Array
      const ids = (o.tableId ? [o.tableId] : []).concat(
        Array.isArray(o.assignedTableIds) ? o.assignedTableIds.filter(id => id !== o.tableId) : []
      );
      if (!ids.length) return '<em>frei</em>';
      return ids.map(id => (state.tables.find(t => t.id === id) || {}).name || '?').join(' + ');
    })();
    const statusLabel = ({
      active:    'aktiv',
      completed: 'beendet',
      cancelled: 'storniert'
    })[o.status] || o.status;
    const guestLabel = o.guestName ? escapeHtml(o.guestName) : '<em>Walk-In</em>';
    const phoneBlock = o.guestPhone ? `<div class="t-meta">☎ ${escapeHtml(o.guestPhone)}</div>` : '';
    const notesBlock = o.notes ? `<div class="t-notes">📝 ${escapeHtml(o.notes)}</div>` : '';

    return `
      <tr>
        <td class="t-time">${startTime}<br><span style="font-size:.75rem;font-weight:400;color:var(--ink-soft);">– ${endTime}</span></td>
        <td class="t-name">
          <strong>${guestLabel}</strong>
          ${phoneBlock}
          ${notesBlock}
        </td>
        <td class="t-guests">${o.guests || '?'} Pers.</td>
        <td class="t-tables">${tableName}</td>
        <td class="t-status">${statusLabel}</td>
        <td class="t-check"></td>
      </tr>
    `;
  }).join('');

  const dateLabel = new Date(targetDate + 'T12:00:00').toLocaleDateString('de-DE', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  });

  const html = `<!DOCTYPE html>
<html lang="de"><head><meta charset="UTF-8"><title>Tagesblatt ${targetDate}</title>
<style>
  @page { size: A4; margin: 1.5cm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; color: #1f2a24; margin: 0; padding: 0; }
  .head { display: flex; justify-content: space-between; align-items: baseline; padding-bottom: 12px; border-bottom: 2px solid #1f2a24; margin-bottom: 16px; }
  .head h1 { margin: 0; font-size: 1.6rem; }
  .head .sub { color: #5a6660; }
  .summary { display: flex; gap: 24px; margin-bottom: 12px; font-size: .9rem; }
  .summary div strong { font-size: 1.1rem; }
  table { width: 100%; border-collapse: collapse; font-size: .9rem; }
  th { text-align: left; background: #efece4; padding: 8px 10px; border-bottom: 2px solid #c9c1ad; font-size: .8rem; text-transform: uppercase; letter-spacing: .04em; }
  td { padding: 10px; border-bottom: 1px solid #e3ddd0; vertical-align: top; }
  .t-time { font-weight: 700; font-size: 1.05rem; white-space: nowrap; width: 60px; }
  .t-guests { text-align: center; font-weight: 600; white-space: nowrap; }
  .t-tables { font-weight: 600; }
  .t-status { font-weight: 600; text-align: center; }
  .t-check { width: 28px; }
  .t-check::before { content: ""; display: inline-block; width: 18px; height: 18px; border: 1.5px solid #1f2a24; border-radius: 3px; }
  .t-meta, .t-notes, .t-preorder { font-size: .8rem; color: #5a6660; margin-top: 3px; }
  .t-notes { font-style: italic; }
  .empty { text-align: center; padding: 40px; color: #8a948e; }
  .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #e3ddd0; font-size: .75rem; color: #8a948e; display: flex; justify-content: space-between; }
  .print-bar { background: #1f2a24; color: white; padding: 10px 20px; display: flex; justify-content: space-between; align-items: center; }
  .print-bar button { padding: 6px 14px; background: white; color: #1f2a24; border: 0; border-radius: 4px; cursor: pointer; font-weight: 600; }
  @media print { .print-bar { display: none; } body { padding: 0; } }
</style>
</head><body>
<div class="print-bar">
  <span>Tagesblatt vom ${dateLabel}</span>
  <span>
    <button onclick="window.print()">Drucken</button>
    <button onclick="window.close()" style="background:transparent;color:white;border:1px solid white;">Schließen</button>
  </span>
</div>
<div style="padding: 20px 30px;">
<div class="head">
  <h1>${escapeHtml(restName)} – Tagesblatt</h1>
  <span class="sub">${dateLabel}</span>
</div>
<div class="summary">
  <div><strong>${day.length}</strong> Reservierungen</div>
  <div><strong>${walkIns.length}</strong> Walk-Ins</div>
  <div><strong>${totalGuests + walkInGuests}</strong> Gäste insgesamt</div>
  <div><strong>${util}%</strong> Ø-Auslastung (${cap} Plätze)</div>
</div>
<h2 style="margin:24px 0 8px 0; font-size:1.1rem; color:var(--ink); border-bottom:1px solid var(--border-strong); padding-bottom:6px;">📅 Reservierungen</h2>
${day.length === 0
  ? '<div class="empty">Keine Reservierungen für diesen Tag.</div>'
  : `<table>
      <thead><tr>
        <th>Zeit</th><th>Gast / Notiz</th><th>Pers.</th><th>Tisch(e)</th><th>Status</th><th>✓</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>`}

<h2 style="margin:32px 0 8px 0; font-size:1.1rem; color:var(--ink); border-bottom:1px solid var(--border-strong); padding-bottom:6px;">🚶 Walk-Ins</h2>
${walkIns.length === 0
  ? '<div class="empty" style="padding:24px;">Keine Walk-Ins an diesem Tag.</div>'
  : `<table>
      <thead><tr>
        <th>Zeit</th><th>Gast / Notiz</th><th>Pers.</th><th>Tisch(e)</th><th>Status</th><th>✓</th>
      </tr></thead>
      <tbody>${walkInRows}</tbody>
    </table>`}

<div class="footer">
  <span>Erstellt: ${new Date().toLocaleString('de-DE')}</span>
  <span>${escapeHtml(restName)} · ${escapeHtml(state.settings.restaurantPhone || '')}</span>
</div>
</div>
</body></html>`;

  const popup = window.open('', '_blank', 'width=900,height=1000');
  if (popup) {
    popup.document.write(html);
    popup.document.close();
  } else {
    showToast('Popup blockiert — bitte Popup-Blocker für diese Seite deaktivieren', 'error');
  }
}

if (typeof window !== 'undefined') {
  window.openDailySheet = openDailySheet;
}
